/**
 * BiometricEnrollPrompt — auto-enrollment banner.
 *
 * Shown to authenticated users who:
 *   1. Are on a device with a platform authenticator (FaceID /
 *      fingerprint / Windows Hello), AND
 *   2. Have no enrolled WebAuthn credentials yet, AND
 *   3. Have not dismissed the prompt this session.
 *
 * Clicking "Enable" runs the enrollment ceremony via useWebAuthn. On
 * success the banner naturally disappears because credentials.length
 * becomes > 0. "Not now" sets a sessionStorage flag so the banner stays
 * dismissed until next page load (the user gets one chance per session
 * — light touch, not intrusive).
 *
 * The banner is rendered from AppLayout so it's visible on every
 * authenticated route. The point is convenience: the user just signed
 * in with their PIN, and we want to make biometric the default for
 * next time without forcing it.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation }     from 'react-i18next'
import { useWebAuthn }        from '@/hooks/useWebAuthn'
import { useAuthStore }       from '@/store/authStore'

const DISMISS_STORAGE_KEY = 'rro:biometric-enroll-dismissed'

function defaultDeviceName(): string {
  if (typeof navigator === 'undefined') return 'This device'
  const ua = navigator.userAgent
  if (/iPhone/.test(ua))  return 'iPhone'
  if (/iPad/.test(ua))    return 'iPad'
  if (/Android/.test(ua)) return 'Android device'
  if (/Mac/.test(ua))     return 'Mac'
  if (/Windows/.test(ua)) return 'Windows PC'
  return 'This device'
}

export function BiometricEnrollPrompt(): ReactNode {
  const { t }       = useTranslation()
  const userId      = useAuthStore((s) => s.user?.id ?? null)
  const userEmail   = useAuthStore((s) => s.user?.email ?? null)
  const { supported, credentials, enrollBiometric, loading, error } =
    useWebAuthn(userId, userEmail)

  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof sessionStorage === 'undefined') return false
    try { return sessionStorage.getItem(DISMISS_STORAGE_KEY) === '1' }
    catch { return false }
  })

  // If a credential lands while the banner was shown (enrollment
  // succeeded), clear the dismiss flag so a future "all credentials
  // revoked" state shows the banner again.
  //
  // Split by kind: the React state is adjusted during render on the
  // no-credentials -> has-credentials transition, while the sessionStorage
  // write stays in an effect, which is where writes to an external system
  // belong. Keying on the transition rather than on `dismissed` also stops
  // the old effect's self-retrigger — it listed `dismissed` as a dependency
  // and then set it, so it re-ran itself once on every enrollment.
  const hasCredentials = credentials.length > 0
  const [prevHasCredentials, setPrevHasCredentials] = useState(hasCredentials)
  if (hasCredentials !== prevHasCredentials) {
    setPrevHasCredentials(hasCredentials)
    if (hasCredentials && dismissed) setDismissed(false)
  }

  useEffect(() => {
    if (!hasCredentials) return
    try { sessionStorage.removeItem(DISMISS_STORAGE_KEY) } catch { /* sessionStorage unavailable */ }
  }, [hasCredentials])

  if (!userId)                  return null
  if (supported === null)       return null   // still probing
  if (!supported)               return null
  if (credentials.length > 0)   return null   // user already has biometric set up
  if (dismissed)                return null

  const handleEnroll = async (): Promise<void> => {
    try {
      await enrollBiometric(defaultDeviceName())
      // Banner disappears via the credentials.length > 0 check.
    } catch {
      // error rendered below.
    }
  }

  const handleDismiss = (): void => {
    try { sessionStorage.setItem(DISMISS_STORAGE_KEY, '1') } catch { /* sessionStorage unavailable */ }
    setDismissed(true)
  }

  return (
    <aside
      role="region"
      aria-label={t('biometric.promptLabel', { defaultValue: 'Biometric sign-in' })}
      style={{
        background:    'var(--brand-primary-subtle, #eff6ff)',
        color:         'var(--brand-neutral-900)',
        borderBottom:  '1px solid var(--brand-primary)',
        padding:       '0.75rem 1rem',
        display:       'flex',
        alignItems:    'center',
        gap:           '0.75rem',
        flexWrap:      'wrap',
      }}
    >
      <div style={{ flex: '1 1 16rem', fontSize: '0.9rem' }}>
        <strong>
          {t('biometric.promptTitle', { defaultValue: 'Speed up next sign-in' })}
        </strong>
        <span style={{ marginLeft: '0.5rem', color: 'var(--brand-neutral-700)' }}>
          {t('biometric.promptBody', {
            defaultValue: 'Enable Face ID / fingerprint to sign in without typing your PIN.',
          })}
        </span>
        {error && (
          <div role="alert" style={{ marginTop: '0.25rem', color: 'var(--brand-danger)' }}>
            {error.userMessage}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          type="button"
          disabled={loading}
          onClick={() => void handleEnroll()}
          style={{
            padding:      '0.4rem 0.9rem',
            background:   'var(--brand-primary)',
            color:        'var(--brand-white)',
            border:       'none',
            borderRadius: 'var(--brand-radius-md)',
            fontSize:     '0.85rem',
            fontWeight:   600,
            cursor:       loading ? 'not-allowed' : 'pointer',
            opacity:      loading ? 0.6 : 1,
          }}
        >
          {loading
            ? t('biometric.promptEnrolling', { defaultValue: 'Setting up…' })
            : t('biometric.promptEnable', { defaultValue: 'Enable' })}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            padding:      '0.4rem 0.9rem',
            background:   'transparent',
            color:        'var(--brand-neutral-700)',
            border:       '1px solid var(--brand-neutral-300)',
            borderRadius: 'var(--brand-radius-md)',
            fontSize:     '0.85rem',
            cursor:       'pointer',
          }}
        >
          {t('biometric.promptNotNow', { defaultValue: 'Not now' })}
        </button>
      </div>
    </aside>
  )
}
